import { Container, ContainerIterator } from "../../ContainerBase";
import checkObject from "../../../utils/checkObject";
import $checkWithinAccessParams from "../../../utils/checkParams.macro";
import { throwIteratorAccessError } from "../../../utils/throwError";
export class HashContainerIterator extends ContainerIterator {
    /**
     * @internal
     */
    constructor(node, header, iteratorType) {
        super(iteratorType);
        this._node = node;
        this._header = header;
        if (this.iteratorType === 0 /* IteratorType.NORMAL */) {
            this.pre = function () {
                if (this._node._pre === this._header) {
                    throwIteratorAccessError();
                }
                this._node = this._node._pre;
                return this;
            };
            this.next = function () {
                if (this._node === this._header) {
                    throwIteratorAccessError();
                }
                this._node = this._node._next;
                return this;
            };
        }
        else {
            this.pre = function () {
                if (this._node._next === this._header) {
                    throwIteratorAccessError();
                }
                this._node = this._node._next;
                return this;
            };
            this.next = function () {
                if (this._node === this._header) {
                    throwIteratorAccessError();
                }
                this._node = this._node._pre;
                return this;
            };
        }
    }
    isAccessible() {
        return this._node !== this._header;
    }
}
export class HashContainer extends Container {
    /**
     * @internal
     */
    constructor() {
        super();
        /**
         * @internal
         */
        this._objMap = [];
        /**
         * @internal
         */
        this._originMap = {};
        /**
         * @description Unique symbol used to tag object.
         */
        this.HASH_TAG = Symbol('@@HASH_TAG');
        Object.setPrototypeOf(this._originMap, null);
        this._header = {};
        this._header._pre = this._header._next = this._head = this._tail = this._header;
    }
    /**
     * @internal
     */
    _eraseNode(node) {
        const { _pre, _next } = node;
        _pre._next = _next;
        _next._pre = _pre;
        if (node === this._head) {
            this._head = _next;
        }
        if (node === this._tail) {
            this._tail = _pre;
        }
        this._length -= 1;
    }
    /**
     * @internal
     */
    _set(key, value, isObject) {
        if (isObject === undefined)
            isObject = checkObject(key);
        let newTail;
        if (isObject) {
            const index = key[this.HASH_TAG];
            if (index !== undefined) {
                this._objMap[index]._value = value;
                return this._length;
            }
            Object.defineProperty(key, this.HASH_TAG, {
                value: this._objMap.length,
                configurable: true
            });
            newTail = {
                _key: key,
                _value: value,
                _pre: this._tail,
                _next: this._header
            };
            this._objMap.push(newTail);
        }
        else {
            const node = this._originMap[key];
            if (node) {
                node._value = value;
                return this._length;
            }
            this._originMap[key] = newTail = {
                _key: key,
                _value: value,
                _pre: this._tail,
                _next: this._header
            };
        }
        if (this._length === 0) {
            this._head = newTail;
            this._header._next = newTail;
        }
        else {
            this._tail._next = newTail;
        }
        this._tail = newTail;
        this._header._pre = newTail;
        return ++this._length;
    }
    /**
     * @internal
     */
    _findElementNode(key, isObject) {
        if (isObject === undefined)
            isObject = checkObject(key);
        if (isObject) {
            const index = key[this.HASH_TAG];
            if (index === undefined)
                return this._header;
            return this._objMap[index];
        }
        else {
            return this._originMap[key] || this._header;
        }
    }
    clear() {
        const HASH_TAG = this.HASH_TAG;
        this._objMap.forEach(function (el) {
            delete el._key[HASH_TAG];
        });
        this._objMap = [];
        this._originMap = {};
        Object.setPrototypeOf(this._originMap, null);
        this._length = 0;
        this._head = this._tail = this._header._pre = this._header._next = this._header;
    }
    /**
     * @description Remove the element of the specified key.
     * @param key - The key you want to remove.
     * @param isObject - Tell us if the type of inserted key is `object` to improve efficiency.<br/>
     *                   If a `undefined` value is passed in, the type will be automatically judged.
     * @returns Whether erase successfully.
     */
    eraseElementByKey(key, isObject) {
        let node;
        if (isObject === undefined)
            isObject = checkObject(key);
        if (isObject) {
            const index = key[this.HASH_TAG];
            if (index === undefined)
                return false;
            delete key[this.HASH_TAG];
            node = this._objMap[index];
            delete this._objMap[index];
        }
        else {
            node = this._originMap[key];
            if (node === undefined)
                return false;
            delete this._originMap[key];
        }
        this._eraseNode(node);
        return true;
    }
    eraseElementByIterator(iter) {
        const node = iter._node;
        if (node === this._header) {
            throwIteratorAccessError();
        }
        this._eraseNode(node);
        return iter.next();
    }
    eraseElementByPos(pos) {
        if (pos < 0 || pos > this._length - 1) {
            throw new RangeError();
        }
        let node = this._head;
        while (pos--) {
            node = node._next;
        }
        this._eraseNode(node);
        return this._length;
    }
}
